/**
 * VaultEvidenceMap · shows which documents support / weaken each dimension
 * of a scoring engine (IMES, Bancabilité, Opportunités, Carrière).
 *
 * Meant to be dropped inside an engine's score card (or right below it).
 * Each dimension row lists the supporting docs as green chips + weakening
 * docs as amber chips.  Clicking a chip navigates to the vault section of
 * the dossier so the user can inspect the source document.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileCheck2, FileWarning, Layers, Loader2 } from "lucide-react";


const DIMENSION_FR = {
  // IMES
  capital_humain:         "Capital humain",
  capital_financier:      "Capital financier",
  capital_relationnel:    "Capital relationnel",
  capital_operationnel:   "Capital opérationnel",
  capital_reputationnel:  "Capital réputationnel",
  capital_reglementaire:  "Capital réglementaire",
  capital_geographique:   "Capital géographique",
  capital_temporel:       "Capital temporel",
  // Bancabilité
  preparation:  "Préparation",
  capacity:     "Capacité",
  collateral:   "Collatéral",
  debt:         "Dette",
  history:      "Historique",
  coherence:    "Cohérence",
  // Opportunities
  eligibility_admissibility:  "Admissibilité",
  financial_capacity:         "Capacité financière",
  professional_profile:       "Profil professionnel",
  project_readiness:          "Maturité du projet",
  // Career
  qualifications:  "Qualifications",
  experience:      "Expérience",
  compensation:    "Rémunération",
  credentials:     "Titres et licences",
};


export default function VaultEvidenceMap({ dossierId, engine, title, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!dossierId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/vault/evidence-map?dossier_id=${encodeURIComponent(dossierId)}&engine=${engine}`
      );
      setData(data);
    } catch (err) {
      setData({ dimensions: {}, documents_used_count: 0 });
    } finally { setLoading(false); }
  }, [dossierId, engine]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#5B5B62]">
        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} /> Chargement des preuves…
      </div>
    );
  }
  const dims = data?.dimensions || {};
  const totalDocs = data?.documents_used_count || 0;

  if (totalDocs === 0) {
    return (
      <div className={`rounded-lg border border-dashed border-[#E4E1D8] px-3 py-2 text-[11px] text-[#5B5B62] ${compact ? "" : "mt-3"}`}
           data-testid={`vault-evidence-empty-${engine}`}>
        Aucun document n'appuie encore ce score. Ajouter des pièces (diplôme, avis
        fiscal, CV…) augmentera la précision.
      </div>
    );
  }

  return (
    <div className={compact ? "" : "mt-4"} data-testid={`vault-evidence-map-${engine}`}>
      {title !== false && (
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
          <Layers className="h-3 w-3" strokeWidth={2} />
          {title || `Documents utilisés (${totalDocs})`}
        </div>
      )}
      <ul className="mt-2 space-y-1.5">
        {Object.entries(dims).map(([dim, group]) => (
          <li key={dim} className="rounded-lg bg-white/70 border border-[#EDE7D6] px-2.5 py-1.5"
              data-testid={`vault-evidence-dim-${dim}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
              {DIMENSION_FR[dim] || dim}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {group.supporting.map((d) => (
                <a key={d.doc_id} href={`/app/dossiers/${dossierId}#vault-doc-${d.doc_id}`}
                   className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] hover:bg-emerald-100"
                   data-testid={`vault-evidence-support-${d.doc_id}`}>
                  <FileCheck2 className="h-2.5 w-2.5" strokeWidth={2} />
                  <span className="truncate max-w-[10rem]">{d.label}</span>
                  {d.confidence != null && (
                    <span className="font-mono text-[9px] text-emerald-700/60">
                      {d.confidence}%
                    </span>
                  )}
                </a>
              ))}
              {group.weakening.map((d) => (
                <a key={d.doc_id} href={`/app/dossiers/${dossierId}#vault-doc-${d.doc_id}`}
                   className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] hover:bg-amber-100"
                   data-testid={`vault-evidence-weaken-${d.doc_id}`}
                   title={d.reason}>
                  <FileWarning className="h-2.5 w-2.5" strokeWidth={2} />
                  <span className="truncate max-w-[10rem]">{d.label}</span>
                </a>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
